import React, { useState } from "react";
import { ConfigProvider } from "antd";
import { MarketOverviewTable } from "../../../styles/MarketOverview.styled";
import { getNameCompletionColumns } from "./tableConfigs";
import { NameCompletionTableProps } from "../types";
import ConfirmationModal from "../SaveAllConfirmationModal";

const NameCompletionTable: React.FC<NameCompletionTableProps> = ({
  tableData,
  editingData,
  isEditing,
  handleInputChange,
  handleSaveDefaultValue,
  handleApplyDefaultToAll,
  handleApplyDefaultToRow,
  handleApplyDefaultToEditingRow,
}) => {
  const [isApplyToAllConfirmOpen, setIsApplyToAllConfirmOpen] = useState(false);
  const [isApplyDefaultConfirmOpen, setIsApplyDefaultConfirmOpen] = useState(false);
  const [pendingApplyDefaultKey, setPendingApplyDefaultKey] = useState<string | null>(null);

  const handleConfirmApplyToAll = () => {
    setIsApplyToAllConfirmOpen(false);
    handleApplyDefaultToAll();
  };

  const handleConfirmApplyDefault = () => {
    if (pendingApplyDefaultKey) {
      setIsApplyDefaultConfirmOpen(false);
      handleApplyDefaultToRow(pendingApplyDefaultKey);
      setPendingApplyDefaultKey(null);
    }
  };

  return (
    <div className="bg-white p-6 rounded-3xl border border-[rgba(0,0,0,0.05)] shadow-[2px_4px_12px_0px_rgba(0,0,0,0.02)] mb-8">
      <div className="flex justify-between items-center mb-6">
        <div className="text-base font-semibold text-[#000000d9] font-[Inter] leading-8">
          Name Completion Score Weights
        </div>
      </div>

      <ConfirmationModal
        open={isApplyToAllConfirmOpen}
        onCancel={() => setIsApplyToAllConfirmOpen(false)}
        onConfirm={handleConfirmApplyToAll}
        title="Apply to All Countries?"
        confirmButtonText="Apply to All"
        content={
          <>
            <span className="text-[rgba(0,0,0,0.9)] text-sm font-normal leading-5">
              This will copy the current default values to{" "}
            </span>
            <span className="text-[rgba(0,0,0,0.9)] text-sm font-medium leading-5">
              all country rows
            </span>
            <span className="text-[rgba(0,0,0,0.9)] text-sm font-normal leading-5">
              . You can revert by clicking Discard.
            </span>
          </>
        }
      />

      <ConfirmationModal
        open={isApplyDefaultConfirmOpen}
        onCancel={() => { setIsApplyDefaultConfirmOpen(false); setPendingApplyDefaultKey(null); }}
        onConfirm={handleConfirmApplyDefault}
        title="Apply Default Value Here?"
        confirmButtonText="Apply default value"
        content={
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            <div className="self-stretch justify-start text-[rgba(0,0,0,0.9)] text-sm font-normal leading-5">
              This will revert this country to the default weights and trigger a pipeline re-run. This may take 30–60 minutes.
            </div>
            <div className="self-stretch justify-start text-[rgba(0,0,0,0.9)] text-sm font-normal leading-5">
              Are you sure you want to proceed?
            </div>
          </div>
        }
      />

      <ConfigProvider
        theme={{
          token: { fontFamily: "Inter" },
          components: {
            Table: {
              headerBorderRadius: 12,
              cellPaddingBlock: 8,
              cellPaddingInline: 10,
            },
          },
        }}
      >
        <MarketOverviewTable
          columns={getNameCompletionColumns({
            isEditing,
            editingData,
            handleInputChange,
            handleSaveDefaultValue,
            handleApplyDefaultToAll,
            handleApplyDefaultToEditingRow,
            handleApplyDefaultToRow,
            setIsApplyDefaultConfirmOpen,
            setPendingApplyDefaultKey,
            setIsApplyToAllConfirmOpen,
          })}
          dataSource={tableData}
          pagination={false}
          size="small"
          rowClassName="text-center"
          bordered={true}
          scroll={{ x: "max-content" }}
        />
        <p className="mt-2 text-[0.75rem] font-semibold">
          Each sub-component contributes to the Name Completeness score based on its assigned weight.
        </p>
        <p className="text-[0.75rem]">
          Configure weights separately for Restaurant and Grocery segments. Each group must total 100%.
        </p>
      </ConfigProvider>
    </div>
  );
};

export default NameCompletionTable;
